/**
 * Event Delegation System
 *
 * A data-attribute driven approach to event handling that avoids inline handlers
 * and centralizes behavior in a single location.
 *
 * Supported attributes:
 * - data-action="name"           Click/Enter/Space triggers handler
 * - data-disallow-action="name"  Prevents action from firing within this region
 * - data-key-action="Key:name"   Keyboard shortcut triggers handler
 * - data-active-class="class"    Key action only fires when element has this class
 *
 * @example
 * // HTML
 * <button data-action="open-sidebar">Menu</button>
 * <aside data-key-action="Escape:close-sidebar" data-active-class="open">
 *
 * // JS
 * import { registerHandler } from './delegator.js';
 * registerHandler('open-sidebar', (el, event) => { ... });
 */

/**
 * Action handlers mapped by name.
 * @type {Object.<string, function(Element, Event): void>}
 */
const handlers = {};

/**
 * Registers an action handler.
 *
 * @param {string} name - The action name (matches data-action attribute)
 * @param {function(Element, Event): void} handler - The handler function
 */
export function registerHandler(name, handler) {
  handlers[name] = handler;
}

/**
 * Checks if an action is disallowed within the event target's ancestry.
 * Elements with data-disallow-action="action1 action2" block those actions
 * from bubbling through.
 *
 * @param {string} action - The action name to check
 * @param {Event} event - The original DOM event
 * @returns {boolean} True if the action is disallowed
 */
function isDisallowed(action, event) {
  const block = event.target.closest('[data-disallow-action]');
  if (!block) return false;

  const disallowed = block.dataset.disallowAction
    .split(/\s+/)
    .filter(Boolean);

  return disallowed.includes(action);
}

/**
 * Dispatches an action to its handler if not disallowed.
 *
 * @param {string} action - The action name
 * @param {Element} el - The element with data-action attribute
 * @param {Event} event - The original DOM event
 */
function dispatch(action, el, event) {
  const handler = handlers[action];

  if (isDisallowed(action, event)) return;

  if (handler) {
    handler(el, event);
  }
}

/**
 * Finds the nearest ancestor (or self) with a data-action attribute
 * that is not disabled.
 *
 * @param {Event} event - The original DOM event
 * @returns {Element|null} The action element or null
 */
function findActionEl(event) {
  return event.target.closest('[data-action]:not([disabled])');
}

/**
 * Initializes the event delegation system.
 * Call this once when the DOM is ready.
 */
export function init() {
  // Click delegation
  document.addEventListener('click', (event) => {
    const el = findActionEl(event);
    if (!el) return;
    dispatch(el.dataset.action, el, event);
  });

  // Keyboard delegation
  document.addEventListener('keydown', (event) => {
    // Element-scoped key bindings (data-key-action="Key:action")
    const els = document.querySelectorAll('[data-key-action]');
    for (const el of els) {
      const activeClass = el.dataset.activeClass;
      if (activeClass && !el.classList.contains(activeClass)) continue;

      const [key, action] = el.dataset.keyAction.split(':');
      if (event.key === key && handlers[action]) {
        handlers[action](el, event);
        return;
      }
    }

    // Enter/Space on focused elements with data-action (accessibility)
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const el = findActionEl(event);
    if (!el) return;

    // Native controls handle their own keyboard events
    if (el.matches('button, a, input, textarea, select')) return;

    if (event.key === ' ') event.preventDefault();
    dispatch(el.dataset.action, el, event);
  });
}
